/**
 * Export column registry.
 *
 * The single source of truth for BOTH the column-picker UI and the CSV writer.
 * Keeping one list means a column can never appear in the picker but be missing
 * from the file (or vice versa), and the file's column order always matches the
 * order the operator saw.
 *
 * Accessors return STRINGS, always. Returning `undefined` for a null field is
 * how `"undefined"` ends up as a literal cell value in an exported report —
 * most columns here are nullable (`dropoff_address`, `flight_number`,
 * `amount_paid`, every `utm_*`), so this matters more than it looks.
 */

import type { ReportResource } from '../types/reporting'

/** A row as returned by the query builders — permissive by design. */
export type ExportRow = Readonly<Record<string, unknown>>

export type ColumnGroup =
  | 'Identity'
  | 'Customer'
  | 'Trip'
  | 'Money'
  | 'Status'
  | 'Attribution'
  | 'Timestamps'

export interface ExportColumn {
  readonly key: string
  readonly label: string
  readonly group: ColumnGroup
  readonly accessor: (row: ExportRow) => string
}

// =============================================================================
// Formatting helpers
// =============================================================================

const str = (value: unknown): string => {
  if (value === null || value === undefined) return ''
  return String(value)
}

/** Fixed 2dp, unquoted, so spreadsheets read it as a number. */
const money = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return ''
  const n = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(n) ? n.toFixed(2) : ''
}

const num = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return ''
  const n = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(n) ? String(n) : ''
}

const bool = (value: unknown): string => {
  if (value === null || value === undefined) return ''
  return value === true ? 'Yes' : value === false ? 'No' : ''
}

/**
 * `YYYY-MM-DD HH:mm` in UTC — unambiguous and parsed as a datetime by Excel,
 * Sheets and LibreOffice alike. ISO-8601 with a `T` and `Z` is frequently
 * imported as text instead, which breaks date sorting in the operator's sheet.
 */
const dateTime = (value: unknown): string => {
  const raw = str(value)
  if (raw === '') return ''
  const d = new Date(raw)
  if (Number.isNaN(d.getTime())) return ''
  const pad = (n: number): string => String(n).padStart(2, '0')
  return (
    `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}` +
    ` ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`
  )
}

/** Read an embedded relation, e.g. `vehicles(name)`. */
const nested = (row: ExportRow, relation: string, field: string): string => {
  const rel = row[relation]
  if (rel === null || rel === undefined) return ''
  // PostgREST returns an object for to-one and an array for to-many embeds.
  const target = Array.isArray(rel) ? rel[0] : rel
  if (target === null || typeof target !== 'object') return ''
  return str((target as Record<string, unknown>)[field])
}

const plain =
  (field: string, format: (v: unknown) => string = str) =>
  (row: ExportRow): string =>
    format(row[field])

// =============================================================================
// Bookings
// =============================================================================

export const BOOKING_EXPORT_COLUMNS: readonly ExportColumn[] = [
  // Identity
  { key: 'confirmation_code', label: 'Confirmation Code', group: 'Identity', accessor: plain('confirmation_code') },
  { key: 'id', label: 'Booking ID', group: 'Identity', accessor: plain('id') },

  // Customer
  { key: 'customer_name', label: 'Customer Name', group: 'Customer', accessor: plain('customer_name') },
  { key: 'customer_email', label: 'Customer Email', group: 'Customer', accessor: plain('customer_email') },
  { key: 'customer_phone', label: 'Customer Phone', group: 'Customer', accessor: plain('customer_phone') },

  // Trip
  { key: 'pickup_datetime', label: 'Pickup Date/Time (UTC)', group: 'Trip', accessor: plain('pickup_datetime', dateTime) },
  { key: 'pickup_address', label: 'Pickup Address', group: 'Trip', accessor: plain('pickup_address') },
  { key: 'dropoff_address', label: 'Dropoff Address', group: 'Trip', accessor: plain('dropoff_address') },
  { key: 'pickup_lat', label: 'Pickup Latitude', group: 'Trip', accessor: plain('pickup_lat', num) },
  { key: 'pickup_lng', label: 'Pickup Longitude', group: 'Trip', accessor: plain('pickup_lng', num) },
  { key: 'dropoff_lat', label: 'Dropoff Latitude', group: 'Trip', accessor: plain('dropoff_lat', num) },
  { key: 'dropoff_lng', label: 'Dropoff Longitude', group: 'Trip', accessor: plain('dropoff_lng', num) },
  { key: 'service_type', label: 'Service Type', group: 'Trip', accessor: plain('service_type') },
  { key: 'vehicle', label: 'Vehicle', group: 'Trip', accessor: (row) => nested(row, 'vehicles', 'name') },
  { key: 'passenger_count', label: 'Passengers', group: 'Trip', accessor: plain('passenger_count', num) },
  { key: 'distance_miles', label: 'Distance (mi)', group: 'Trip', accessor: plain('distance_miles', num) },
  { key: 'duration_hours', label: 'Duration (hrs)', group: 'Trip', accessor: plain('duration_hours', num) },
  { key: 'return_trip', label: 'Round Trip', group: 'Trip', accessor: plain('return_trip', bool) },
  { key: 'return_datetime', label: 'Return Date/Time (UTC)', group: 'Trip', accessor: plain('return_datetime', dateTime) },
  { key: 'flight_number', label: 'Flight Number', group: 'Trip', accessor: plain('flight_number') },
  { key: 'airline', label: 'Airline', group: 'Trip', accessor: plain('airline') },
  { key: 'is_airport_pickup', label: 'Airport Pickup', group: 'Trip', accessor: plain('is_airport_pickup', bool) },
  { key: 'is_airport_dropoff', label: 'Airport Dropoff', group: 'Trip', accessor: plain('is_airport_dropoff', bool) },
  { key: 'special_requests', label: 'Special Requests', group: 'Trip', accessor: plain('special_requests') },

  // Money
  { key: 'subtotal', label: 'Subtotal', group: 'Money', accessor: plain('subtotal', money) },
  { key: 'gratuity_amount', label: 'Gratuity', group: 'Money', accessor: plain('gratuity_amount', money) },
  { key: 'taxes', label: 'Taxes', group: 'Money', accessor: plain('taxes', money) },
  { key: 'total_amount', label: 'Total', group: 'Money', accessor: plain('total_amount', money) },
  { key: 'amount_paid', label: 'Amount Paid', group: 'Money', accessor: plain('amount_paid', money) },
  { key: 'balance_due', label: 'Balance Due', group: 'Money', accessor: plain('balance_due', money) },
  { key: 'deposit_amount', label: 'Deposit Amount', group: 'Money', accessor: plain('deposit_amount', money) },
  { key: 'deposit_required', label: 'Deposit Required', group: 'Money', accessor: plain('deposit_required', bool) },

  // Status
  { key: 'booking_status', label: 'Booking Status', group: 'Status', accessor: plain('booking_status') },
  { key: 'payment_status', label: 'Payment Status', group: 'Status', accessor: plain('payment_status') },
  { key: 'is_test', label: 'Test Booking', group: 'Status', accessor: plain('is_test', bool) },

  // Attribution — sourced from the embedded booking_quotes row when present.
  { key: 'utm_source', label: 'UTM Source', group: 'Attribution', accessor: (row) => nested(row, 'booking_quotes', 'utm_source') },
  { key: 'utm_medium', label: 'UTM Medium', group: 'Attribution', accessor: (row) => nested(row, 'booking_quotes', 'utm_medium') },
  { key: 'utm_campaign', label: 'UTM Campaign', group: 'Attribution', accessor: (row) => nested(row, 'booking_quotes', 'utm_campaign') },

  // Timestamps
  { key: 'created_at', label: 'Created At (UTC)', group: 'Timestamps', accessor: plain('created_at', dateTime) },
  { key: 'cancelled_at', label: 'Cancelled At (UTC)', group: 'Timestamps', accessor: plain('cancelled_at', dateTime) },
  { key: 'cancellation_reason', label: 'Cancellation Reason', group: 'Timestamps', accessor: plain('cancellation_reason') },
]

/**
 * What a first-time export contains: enough to reconcile a month's revenue
 * without the columns most operators would immediately delete (coordinates,
 * internal IDs, UTM).
 */
export const DEFAULT_BOOKING_COLUMNS: readonly string[] = [
  'confirmation_code',
  'customer_name',
  'customer_email',
  'customer_phone',
  'pickup_datetime',
  'pickup_address',
  'dropoff_address',
  'service_type',
  'vehicle',
  'passenger_count',
  'subtotal',
  'total_amount',
  'amount_paid',
  'balance_due',
  'booking_status',
  'payment_status',
  'created_at',
]

// =============================================================================
// Quotes
// =============================================================================

export const QUOTE_EXPORT_COLUMNS: readonly ExportColumn[] = [
  { key: 'token', label: 'Quote Token', group: 'Identity', accessor: plain('token') },
  { key: 'id', label: 'Quote ID', group: 'Identity', accessor: plain('id') },
  { key: 'booking_id', label: 'Converted Booking ID', group: 'Identity', accessor: plain('booking_id') },

  { key: 'customer_name', label: 'Customer Name', group: 'Customer', accessor: plain('customer_name') },
  { key: 'customer_email', label: 'Customer Email', group: 'Customer', accessor: plain('customer_email') },
  { key: 'customer_phone', label: 'Customer Phone', group: 'Customer', accessor: plain('customer_phone') },

  { key: 'pickup_datetime', label: 'Pickup Date/Time (UTC)', group: 'Trip', accessor: plain('pickup_datetime', dateTime) },
  { key: 'pickup_address', label: 'Pickup Address', group: 'Trip', accessor: plain('pickup_address') },
  { key: 'dropoff_address', label: 'Dropoff Address', group: 'Trip', accessor: plain('dropoff_address') },
  { key: 'pickup_lat', label: 'Pickup Latitude', group: 'Trip', accessor: plain('pickup_lat', num) },
  { key: 'pickup_lng', label: 'Pickup Longitude', group: 'Trip', accessor: plain('pickup_lng', num) },
  { key: 'dropoff_lat', label: 'Dropoff Latitude', group: 'Trip', accessor: plain('dropoff_lat', num) },
  { key: 'dropoff_lng', label: 'Dropoff Longitude', group: 'Trip', accessor: plain('dropoff_lng', num) },
  { key: 'service_type', label: 'Service Type', group: 'Trip', accessor: plain('service_type') },
  // booking_quotes denormalises vehicle_name, so it survives vehicle deletion —
  // prefer it and fall back to the joined row.
  {
    key: 'vehicle',
    label: 'Vehicle',
    group: 'Trip',
    accessor: (row) => str(row.vehicle_name) || nested(row, 'vehicles', 'name'),
  },
  { key: 'passenger_count', label: 'Passengers', group: 'Trip', accessor: plain('passenger_count', num) },
  { key: 'distance_miles', label: 'Distance (mi)', group: 'Trip', accessor: plain('distance_miles', num) },
  { key: 'duration_hours', label: 'Duration (hrs)', group: 'Trip', accessor: plain('duration_hours', num) },
  { key: 'return_trip', label: 'Round Trip', group: 'Trip', accessor: plain('return_trip', bool) },
  { key: 'is_airport_pickup', label: 'Airport Pickup', group: 'Trip', accessor: plain('is_airport_pickup', bool) },
  { key: 'is_airport_dropoff', label: 'Airport Dropoff', group: 'Trip', accessor: plain('is_airport_dropoff', bool) },
  { key: 'special_requests', label: 'Special Requests', group: 'Trip', accessor: plain('special_requests') },

  { key: 'subtotal', label: 'Subtotal', group: 'Money', accessor: plain('subtotal', money) },
  { key: 'gratuity_amount', label: 'Gratuity', group: 'Money', accessor: plain('gratuity_amount', money) },
  { key: 'taxes', label: 'Taxes', group: 'Money', accessor: plain('taxes', money) },
  { key: 'total_amount', label: 'Total', group: 'Money', accessor: plain('total_amount', money) },
  { key: 'discount_type', label: 'Discount Type', group: 'Money', accessor: plain('discount_type') },
  { key: 'discount_value', label: 'Discount Value', group: 'Money', accessor: plain('discount_value', num) },
  { key: 'is_flat_rate', label: 'Flat Rate', group: 'Money', accessor: plain('is_flat_rate', bool) },
  { key: 'flat_rate_name', label: 'Flat Rate Name', group: 'Money', accessor: plain('flat_rate_name') },
  { key: 'is_operator_priced', label: 'Operator Priced', group: 'Money', accessor: plain('is_operator_priced', bool) },

  { key: 'status', label: 'Status', group: 'Status', accessor: plain('status') },

  { key: 'utm_source', label: 'UTM Source', group: 'Attribution', accessor: plain('utm_source') },
  { key: 'utm_medium', label: 'UTM Medium', group: 'Attribution', accessor: plain('utm_medium') },
  { key: 'utm_campaign', label: 'UTM Campaign', group: 'Attribution', accessor: plain('utm_campaign') },
  { key: 'utm_term', label: 'UTM Term', group: 'Attribution', accessor: plain('utm_term') },
  { key: 'utm_content', label: 'UTM Content', group: 'Attribution', accessor: plain('utm_content') },

  { key: 'created_at', label: 'Created At (UTC)', group: 'Timestamps', accessor: plain('created_at', dateTime) },
  { key: 'expires_at', label: 'Expires At (UTC)', group: 'Timestamps', accessor: plain('expires_at', dateTime) },
  { key: 'converted_at', label: 'Converted At (UTC)', group: 'Timestamps', accessor: plain('converted_at', dateTime) },
  { key: 'priced_at', label: 'Priced At (UTC)', group: 'Timestamps', accessor: plain('priced_at', dateTime) },
]

export const DEFAULT_QUOTE_COLUMNS: readonly string[] = [
  'token',
  'status',
  'customer_name',
  'customer_email',
  'customer_phone',
  'pickup_datetime',
  'pickup_address',
  'dropoff_address',
  'service_type',
  'vehicle',
  'passenger_count',
  'total_amount',
  'utm_source',
  'created_at',
  'expires_at',
]

// =============================================================================
// Lookup
// =============================================================================

export function columnsFor(resource: ReportResource): readonly ExportColumn[] {
  return resource === 'bookings' ? BOOKING_EXPORT_COLUMNS : QUOTE_EXPORT_COLUMNS
}

export function defaultColumnsFor(resource: ReportResource): readonly string[] {
  return resource === 'bookings' ? DEFAULT_BOOKING_COLUMNS : DEFAULT_QUOTE_COLUMNS
}

/**
 * Resolve selected keys to columns, in REGISTRY order (not selection order) and
 * dropping keys that no longer exist. A saved view written before a column was
 * renamed must still export rather than emitting an empty column.
 */
export function resolveColumns(
  resource: ReportResource,
  selectedKeys: readonly string[]
): readonly ExportColumn[] {
  const selected = new Set(selectedKeys)
  return columnsFor(resource).filter((c) => selected.has(c.key))
}

/** Group columns for the picker UI, preserving registry order within a group. */
export function groupColumns(
  columns: readonly ExportColumn[]
): readonly (readonly [ColumnGroup, readonly ExportColumn[]])[] {
  const order: readonly ColumnGroup[] = [
    'Identity',
    'Customer',
    'Trip',
    'Money',
    'Status',
    'Attribution',
    'Timestamps',
  ]
  return order
    .map((group) => [group, columns.filter((c) => c.group === group)] as const)
    .filter(([, cols]) => cols.length > 0)
}
